import { Button, Dialog, Repeat } from "@app/ui";
import { formatDuration } from "../../lib/format.js";
import { useAppLocale } from "../../lib/useAppLocale.js";
import { useI18n } from "../../lib/useI18n.js";
import { useRecurringConfirmStore } from "../../stores/index.js";

/** Confirmation popup for changes that affect a recurring series. */
export function RecurringConfirmDialog() {
  const { t } = useI18n();
  const { open, pending, resolve } = useRecurringConfirmStore();
  const locale = useAppLocale();
  if (!pending) return null;

  const isDelete = pending.kind === "delete";
  const showAll = isDelete
    || pending.kind === "edit"
    || pending.kind === "resize"
    || pending.kind === "reschedule"
    || pending.kind === "convert-all-day";

  // Mirror the record type in the wording: "Only this event" vs "Only this task".
  const noun = pending.task.is_event ? t("common.event").toLowerCase() : t("common.task").toLowerCase();
  const title = pending.task.title || noun;

  const description =
    pending.kind === "reschedule"
      ? t("recurring.move", { title, from: formatFull(new Date(pending.oldOccurrence), locale), to: formatFull(new Date(pending.newDueAt), locale) })
      : pending.kind === "convert-all-day"
        ? t("recurring.moveAllDay", { title, from: formatFull(new Date(pending.oldOccurrence), locale), date: formatDateOnly(new Date(pending.newDueAt), locale) })
      : pending.kind === "resize"
        ? t("recurring.resize", { title, duration: formatDuration(pending.newDurationMinutes) })
        : pending.kind === "delete"
          ? t("recurring.delete", { title })
          : pending.kind === "edit"
            ? t("recurring.edit", { title })
            : t("recurring.rename", { title, nextTitle: pending.newTitle });

  return (
    <Dialog open={open} onClose={() => resolve("cancel")} title={t(isDelete ? "recurring.deleteTitle" : "recurring.editTitle", { noun })} className="max-w-md">
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-2 text-sm">
          <Repeat className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="flex-1">
            <p className="text-foreground">{description}</p>
            <p className="mt-2 text-muted-foreground">
              {t("recurring.partOfSeries", { noun })}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button variant="outline" onClick={() => resolve("single")}>
            {t("recurring.onlyThis", { noun })}
          </Button>
          <Button variant={isDelete ? "outline" : "primary"} onClick={() => resolve("series")}>
            {t("recurring.thisAndFollowing", { noun })}
          </Button>
          {showAll && (
            <Button variant={isDelete ? "destructive" : "outline"} onClick={() => resolve("all")}>
              {isDelete ? t("recurring.wholeSeries") : t("recurring.all", { noun })}
            </Button>
          )}
          <Button variant="ghost" onClick={() => resolve("cancel")}>
            {t("common.cancel")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function formatFull(d: Date, locale: string): string {
  return d.toLocaleString(locale, {
    weekday: "short", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function formatDateOnly(d: Date, locale: string): string {
  return d.toLocaleDateString(locale, {
    weekday: "short", month: "short", day: "numeric",
  });
}
